import React, { useState, useEffect } from "react";
import Navbar from "./Navbar";
import { supabase } from "./supabase";

const categories = [
  "Construction & Materials",
  "Transport & Logistics",
  "Food & Agriculture",
  "Professional Services",
  "Retail & Supplies",
  "Technology",
  "Other",
];

const provinces = ["Gauteng", "Western Cape", "KwaZulu-Natal", "Eastern Cape", "Limpopo", "Mpumalanga", "North West", "Free State", "Northern Cape"];

const inputStyle = {
  width: "100%",
  background: "#F5F0E8",
  border: "1.5px solid #EDE8DF",
  borderRadius: 8,
  padding: "11px 14px",
  color: "#1A1A2E",
  fontSize: 16,
  fontFamily: "var(--font-body)",
  outline: "none",
  marginBottom: 16,
  boxSizing: "border-box",
};

const labelStyle = {
  fontSize: 11,
  color: "#888",
  fontWeight: 700,
  letterSpacing: "0.08em",
  textTransform: "uppercase",
  marginBottom: 6,
  display: "block",
};

export default function Suppliers() {
  const [user, setUser] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [suppliers, setSuppliers] = useState([]);
  const [categoryFilter, setCategoryFilter] = useState("All categories");
  const [provinceFilter, setProvinceFilter] = useState("All provinces");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(false);
  const [form, setForm] = useState({ business_name: "", category: "", province: "", contact_info: "", description: "" });

  // --- My business / banking details state ---
  const [mySuppliers, setMySuppliers] = useState([]);
  const [myBanking, setMyBanking] = useState({}); // supplier_id -> banking row
  const [bankingFormFor, setBankingFormFor] = useState(null); // supplier_id currently editing
  const [bankingForm, setBankingForm] = useState({ account_holder_name: "", bank_name: "", account_number: "", branch_code: "" });
  const [bankingError, setBankingError] = useState("");
  const [bankingLoading, setBankingLoading] = useState(false);
  const [bankingSuccess, setBankingSuccess] = useState(false);

  useEffect(() => {
    supabase.auth.getUser().then(({ data: { user } }) => {
      setUser(user);
      if (user) fetchMyBusiness(user.id);
    });
    fetchSuppliers();
  }, []);

  const fetchSuppliers = async () => {
    const { data } = await supabase.from("suppliers").select("*").order("created_at", { ascending: false });
    if (data) setSuppliers(data);
  };

  const fetchMyBusiness = async (userId) => {
    const { data: mine } = await supabase.from("suppliers").select("*").eq("user_id", userId);
    if (!mine) return;
    setMySuppliers(mine);

    const ids = mine.map((s) => s.id);
    if (ids.length === 0) return;

    const { data: banking } = await supabase.from("supplier_banking_details").select("*").in("supplier_id", ids);
    if (banking) {
      const map = {};
      banking.forEach((b) => { map[b.supplier_id] = b; });
      setMyBanking(map);
    }
  };

  const handleSubmit = async () => {
    if (!form.business_name || !form.category || !form.province || !form.contact_info || !form.description) {
      setError("Please fill in all fields.");
      return;
    }
    if (form.description.length < 20) {
      setError("Please write at least 20 characters describing your business.");
      return;
    }
    setLoading(true);
    setError("");
    const { error: submitError } = await supabase.from("suppliers").insert({
      user_id: user.id,
      business_name: form.business_name,
      category: form.category,
      province: form.province,
      contact_info: form.contact_info,
      description: form.description,
      verified: false,
    });
    setLoading(false);
    if (submitError) {
      setError(submitError.message);
    } else {
      setSuccess(true);
      setShowForm(false);
      setForm({ business_name: "", category: "", province: "", contact_info: "", description: "" });
      fetchSuppliers();
      fetchMyBusiness(user.id);
    }
  };

  const openBankingForm = (supplierId) => {
    const existing = myBanking[supplierId];
    setBankingForm({
      account_holder_name: existing?.account_holder_name || "",
      bank_name: existing?.bank_name || "",
      account_number: existing?.account_number || "",
      branch_code: existing?.branch_code || "",
    });
    setBankingError("");
    setBankingSuccess(false);
    setBankingFormFor(supplierId);
  };

  const submitBanking = async (supplierId) => {
    if (!bankingForm.account_holder_name || !bankingForm.bank_name || !bankingForm.account_number || !bankingForm.branch_code) {
      setBankingError("Please fill in all fields.");
      return;
    }
    setBankingLoading(true);
    setBankingError("");

    const existing = myBanking[supplierId];
    let submitError;

    if (existing) {
      const { error } = await supabase.from("supplier_banking_details").update({
        account_holder_name: bankingForm.account_holder_name,
        bank_name: bankingForm.bank_name,
        account_number: bankingForm.account_number,
        branch_code: bankingForm.branch_code,
        updated_at: new Date().toISOString(),
      }).eq("id", existing.id);
      submitError = error;
    } else {
      const { error } = await supabase.from("supplier_banking_details").insert({
        supplier_id: supplierId,
        account_holder_name: bankingForm.account_holder_name,
        bank_name: bankingForm.bank_name,
        account_number: bankingForm.account_number,
        branch_code: bankingForm.branch_code,
      });
      submitError = error;
    }

    setBankingLoading(false);
    if (submitError) {
      setBankingError(submitError.message);
    } else {
      setBankingSuccess(true);
      setBankingFormFor(null);
      fetchMyBusiness(user.id);
    }
  };

  const filtered = suppliers.filter((s) => {
    const matchCategory = categoryFilter === "All categories" || s.category === categoryFilter;
    const matchProvince = provinceFilter === "All provinces" || s.province === provinceFilter;
    return matchCategory && matchProvince;
  });

  return (
    <div style={{ fontFamily: "var(--font-body)", background: "#F5F0E8", minHeight: "100vh", color: "#1A1A2E" }}>

      <style>{`
        .supp-header { padding: 56px 48px 48px; }
        .supp-success { max-width: 700px; margin: 32px auto 0; padding: 16px 24px; }
        .supp-submit { max-width: 1100px; margin: 0 auto; padding: 32px 48px 0; }
        .supp-form { background: #fff; border: 1px solid #EDE8DF; border-radius: 20px; padding: 32px; }
        .supp-submitbar { background: #fff; border: 1px solid #EDE8DF; border-radius: 16px; padding: 20px 24px; display: flex; align-items: center; justify-content: space-between; gap: 16px; flex-wrap: wrap; }
        .supp-mybiz { max-width: 1100px; margin: 0 auto; padding: 24px 48px 0; }
        .supp-mybiz-card { background: #fff; border: 1px solid #EDE8DF; border-radius: 16px; padding: 20px 24px; margin-bottom: 12px; }
        .supp-bank-form { background: #F5F0E8; border-radius: 12px; padding: 18px; margin-top: 14px; }
        .supp-filters { max-width: 1100px; margin: 0 auto; padding: 24px 48px 0; display: flex; gap: 12px; align-items: center; flex-wrap: wrap; }
        .supp-main { max-width: 1100px; margin: 0 auto; padding: 16px 48px 72px; }
        .supp-grid { display: grid; grid-template-columns: repeat(auto-fit, minmax(220px, 1fr)); gap: 16px; }
        .supp-select { padding: 7px 14px; border: 1px solid #EDE8DF; border-radius: 8px; font-size: 12px; background: #fff; color: #1A1A2E; outline: none; font-family: var(--font-body); cursor: pointer; }
        @media (max-width: 768px) {
          .supp-header { padding: 36px 20px 32px; }
          .supp-success { margin: 20px 20px 0; }
          .supp-submit { padding: 20px 20px 0; }
          .supp-form { padding: 20px; }
          .supp-submitbar { flex-direction: column; align-items: flex-start; }
          .supp-submitbar button { width: 100%; text-align: center; }
          .supp-mybiz { padding: 20px 20px 0; }
          .supp-mybiz-card { padding: 16px 18px; }
          .supp-filters { padding: 16px 20px 0; }
          .supp-filters select { width: 100%; font-size: 16px; }
          .supp-main { padding: 16px 20px 48px; }
          .supp-grid { grid-template-columns: 1fr; gap: 12px; }
        }
      `}</style>

      <Navbar />

      {/* HEADER */}
      <div className="supp-header" style={{ background: "#fff", borderBottom: "1px solid #EDE8DF", textAlign: "center" }}>
        <div style={{ display: "inline-block", background: "rgba(27,58,107,0.07)", border: "1px solid rgba(27,58,107,0.15)", borderRadius: 99, padding: "5px 16px", fontSize: 11, color: "#1B3A6B", fontWeight: 700, letterSpacing: "0.1em", textTransform: "uppercase", marginBottom: 20 }}>
          Suppliers
        </div>
        <h1 style={{ fontSize: "clamp(26px, 4vw, 44px)", fontWeight: 900, color: "#1A1A2E", letterSpacing: "-0.02em", margin: "0 0 16px", lineHeight: 1.1 }}>
          Local Business <span style={{ color: "#1B3A6B" }}>Directory</span>
        </h1>
        <p style={{ fontSize: 15, color: "#888", lineHeight: 1.8, maxWidth: 540, margin: "0 auto" }}>
          When Forge SA projects need materials and services, we look here first. Register your business and get discovered by your community.
        </p>
      </div>

      {/* SUCCESS */}
      {success && (
        <div className="supp-success" style={{ background: "rgba(46,125,50,0.07)", border: "1px solid rgba(46,125,50,0.2)", borderRadius: 12, textAlign: "center", color: "#2E7D32", fontWeight: 700 }}>
          Thank you! Your business has been submitted and is pending verification.
        </div>
      )}

      {/* SUBMIT BAR */}
      <div className="supp-submit">
        {!showForm ? (
          <div className="supp-submitbar">
            <div style={{ fontSize: 13, color: "#4A4A6A" }}>
              Run a business or offer a service? <strong style={{ color: "#1A1A2E" }}>Register and get found first.</strong>
            </div>
            <button
              onClick={() => user ? setShowForm(true) : window.location.href = "/login"}
              style={{ background: "#1B3A6B", color: "#fff", border: "none", borderRadius: 99, padding: "10px 22px", fontSize: 12, fontWeight: 700, fontFamily: "var(--font-body)", cursor: "pointer", whiteSpace: "nowrap" }}
            >
              + Register your business
            </button>
          </div>
        ) : (
          <div className="supp-form">
            <div style={{ fontSize: 18, fontWeight: 800, color: "#1A1A2E", marginBottom: 24 }}>Register Your Business</div>

            <label style={labelStyle}>Business Name</label>
            <input style={inputStyle} value={form.business_name} onChange={(e) => setForm({ ...form, business_name: e.target.value })} placeholder="e.g. Mokoena Hardware Supplies" />

            <label style={labelStyle}>Category</label>
            <select style={inputStyle} value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })}>
              <option value="">Select category</option>
              {categories.map(c => <option key={c} value={c}>{c}</option>)}
            </select>

            <label style={labelStyle}>Province</label>
            <select style={inputStyle} value={form.province} onChange={(e) => setForm({ ...form, province: e.target.value })}>
              <option value="">Select province</option>
              {provinces.map(p => <option key={p} value={p}>{p}</option>)}
            </select>

            <label style={labelStyle}>Contact Information</label>
            <input style={inputStyle} value={form.contact_info} onChange={(e) => setForm({ ...form, contact_info: e.target.value })} placeholder="Phone number or email" />

            <label style={labelStyle}>Describe your business</label>
            <textarea
              style={{ ...inputStyle, resize: "vertical" }}
              value={form.description}
              onChange={(e) => setForm({ ...form, description: e.target.value })}
              placeholder="What does your business offer, and what areas do you serve?"
              rows={4}
            />
            <div style={{ fontSize: 11, color: "#888", marginTop: -12, marginBottom: 16 }}>{form.description.length} characters</div>

            {error && (
              <div style={{ background: "rgba(192,57,43,0.07)", border: "1px solid rgba(192,57,43,0.2)", borderRadius: 8, padding: "12px 16px", marginBottom: 16, fontSize: 13, color: "#C0392B" }}>
                {error}
              </div>
            )}

            <div style={{ display: "flex", gap: 10 }}>
              <button
                onClick={() => setShowForm(false)}
                style={{ flex: 1, background: "transparent", border: "1.5px solid #EDE8DF", borderRadius: 99, padding: "13px", color: "#888", fontWeight: 600, fontSize: 14, cursor: "pointer", fontFamily: "var(--font-body)" }}
              >
                Cancel
              </button>
              <button
                onClick={handleSubmit}
                disabled={loading}
                style={{ flex: 2, background: loading ? "#EDE8DF" : "#1B3A6B", border: "none", borderRadius: 99, padding: "13px", color: loading ? "#888" : "#fff", fontWeight: 800, fontSize: 14, cursor: loading ? "not-allowed" : "pointer", fontFamily: "var(--font-body)" }}
              >
                {loading ? "Submitting..." : "Submit Registration"}
              </button>
            </div>
          </div>
        )}
      </div>

      {/* MY BUSINESS / BANKING DETAILS */}
      {mySuppliers.length > 0 && (
        <div className="supp-mybiz">
          <div style={{ fontSize: 13, fontWeight: 700, color: "#1A1A2E", marginBottom: 12 }}>Your business</div>
          {mySuppliers.map((s) => {
            const banking = myBanking[s.id];
            const isVerified = banking?.avs_verified;
            return (
              <div key={s.id} className="supp-mybiz-card">
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 12, flexWrap: "wrap" }}>
                  <div>
                    <div style={{ fontSize: 14, fontWeight: 800, color: "#1A1A2E" }}>{s.business_name}</div>
                    <div style={{ fontSize: 12, color: "#888", marginTop: 2 }}>
                      {banking
                        ? (isVerified ? "Banking details verified" : "Banking details submitted, awaiting verification")
                        : "No banking details submitted yet"}
                    </div>
                  </div>
                  {!isVerified && (
                    <button
                      onClick={() => bankingFormFor === s.id ? setBankingFormFor(null) : openBankingForm(s.id)}
                      style={{ background: "rgba(27,58,107,0.07)", border: "1px solid rgba(27,58,107,0.15)", borderRadius: 99, padding: "8px 16px", color: "#1B3A6B", fontWeight: 700, fontSize: 12, cursor: "pointer", fontFamily: "var(--font-body)" }}
                    >
                      {banking ? "Edit banking details" : "Add banking details"}
                    </button>
                  )}
                  {isVerified && (
                    <span style={{ fontSize: 11, background: "rgba(46,125,50,0.08)", color: "#2E7D32", borderRadius: 99, padding: "5px 12px", fontWeight: 700 }}>
                      Verified, locked
                    </span>
                  )}
                </div>

                {bankingFormFor === s.id && (
                  <div className="supp-bank-form">
                    <label style={labelStyle}>Account holder name</label>
                    <input
                      style={inputStyle}
                      value={bankingForm.account_holder_name}
                      onChange={(e) => setBankingForm({ ...bankingForm, account_holder_name: e.target.value })}
                      placeholder="Must match the business or owner's name"
                    />
                    <label style={labelStyle}>Bank name</label>
                    <input
                      style={inputStyle}
                      value={bankingForm.bank_name}
                      onChange={(e) => setBankingForm({ ...bankingForm, bank_name: e.target.value })}
                      placeholder="e.g. Nedbank"
                    />
                    <label style={labelStyle}>Account number</label>
                    <input
                      style={inputStyle}
                      value={bankingForm.account_number}
                      onChange={(e) => setBankingForm({ ...bankingForm, account_number: e.target.value })}
                      placeholder="Account number"
                    />
                    <label style={labelStyle}>Branch code</label>
                    <input
                      style={inputStyle}
                      value={bankingForm.branch_code}
                      onChange={(e) => setBankingForm({ ...bankingForm, branch_code: e.target.value })}
                      placeholder="Branch code"
                    />

                    {bankingError && (
                      <div style={{ background: "rgba(192,57,43,0.07)", border: "1px solid rgba(192,57,43,0.2)", borderRadius: 8, padding: "10px 14px", marginBottom: 14, fontSize: 12, color: "#C0392B" }}>
                        {bankingError}
                      </div>
                    )}

                    <div style={{ fontSize: 11, color: "#888", marginBottom: 14, lineHeight: 1.6 }}>
                      These details will be verified before any payment is made to your business. You can edit them at any time until they're verified.
                    </div>

                    <div style={{ display: "flex", gap: 10 }}>
                      <button
                        onClick={() => setBankingFormFor(null)}
                        style={{ flex: 1, background: "transparent", border: "1.5px solid #EDE8DF", borderRadius: 99, padding: "12px", color: "#888", fontWeight: 600, fontSize: 13, cursor: "pointer", fontFamily: "var(--font-body)" }}
                      >
                        Cancel
                      </button>
                      <button
                        onClick={() => submitBanking(s.id)}
                        disabled={bankingLoading}
                        style={{ flex: 2, background: bankingLoading ? "#EDE8DF" : "#1B3A6B", border: "none", borderRadius: 99, padding: "12px", color: bankingLoading ? "#888" : "#fff", fontWeight: 800, fontSize: 13, cursor: bankingLoading ? "not-allowed" : "pointer", fontFamily: "var(--font-body)" }}
                      >
                        {bankingLoading ? "Saving..." : "Save banking details"}
                      </button>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* FILTERS */}
      <div className="supp-filters">
        <select className="supp-select" value={categoryFilter} onChange={(e) => setCategoryFilter(e.target.value)}>
          <option>All categories</option>
          {categories.map(c => <option key={c}>{c}</option>)}
        </select>
        <select className="supp-select" value={provinceFilter} onChange={(e) => setProvinceFilter(e.target.value)}>
          <option>All provinces</option>
          {provinces.map(p => <option key={p}>{p}</option>)}
        </select>
      </div>

      {/* MAIN */}
      <div className="supp-main">
        <div style={{ fontSize: 13, color: "#888", marginBottom: 16 }}>
          Showing <strong style={{ color: "#1B3A6B" }}>{filtered.length}</strong> supplier{filtered.length !== 1 ? "s" : ""}
        </div>

        {filtered.length === 0 ? (
          <div style={{ textAlign: "center", padding: "64px 0", color: "#888", fontSize: 14 }}>
            No suppliers registered yet. Be the first to list your business.
          </div>
        ) : (
          <div className="supp-grid">
            {filtered.map((s) => (
              <div key={s.id} style={{ background: "#fff", border: "1px solid #EDE8DF", borderRadius: 16, padding: 20, display: "flex", flexDirection: "column", gap: 10 }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 8 }}>
                  <span style={{ display: "inline-block", background: "#F5F0E8", color: "#1B3A6B", fontSize: 10, padding: "3px 10px", borderRadius: 99, fontWeight: 700, letterSpacing: "0.04em" }}>
                    {s.category}
                  </span>
                  {s.verified && (
                    <span style={{ display: "inline-flex", alignItems: "center", gap: 4, background: "rgba(46,125,50,0.08)", color: "#2E7D32", fontSize: 10, padding: "3px 10px", borderRadius: 99, fontWeight: 700, flexShrink: 0 }}>
                      <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="#2E7D32" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                        <polyline points="20 6 9 17 4 12"/>
                      </svg>
                      Verified
                    </span>
                  )}
                </div>
                <div style={{ fontSize: 15, fontWeight: 800, color: "#1A1A2E" }}>{s.business_name}</div>
                <div style={{ fontSize: 12, color: "#888", lineHeight: 1.65, flex: 1 }}>{s.description}</div>
                <div style={{ borderTop: "1px solid #EDE8DF", paddingTop: 10, display: "flex", flexDirection: "column", gap: 4 }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 12, color: "#888" }}>
                    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="#888" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0118 0z"/><circle cx="12" cy="10" r="3"/>
                    </svg>
                    {s.province}
                  </div>
                  <div style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 12, color: "#1B3A6B", fontWeight: 600 }}>
                    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="#1B3A6B" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M22 16.92v3a2 2 0 01-2.18 2 19.79 19.79 0 01-8.63-3.07 19.5 19.5 0 01-6-6 19.79 19.79 0 01-3.07-8.67A2 2 0 014.11 2h3a2 2 0 012 1.72c.127.96.361 1.903.7 2.81a2 2 0 01-.45 2.11L8.09 9.91a16 16 0 006 6l1.27-1.27a2 2 0 012.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0122 16.92z"/>
                    </svg>
                    {s.contact_info}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

    </div>
  );
}